import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { packagesAPI } from '../../services/api';
import { ArrowLeft, Plus, Edit, Trash2, Search, Eye, EyeOff, Calendar, Users, AlertTriangle, Copy, Percent, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPackages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [discountCategory, setDiscountCategory] = useState('all');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [yearFilter, setYearFilter] = useState('all');
  const [maxInterestFree, setMaxInterestFree] = useState(2);
  const [savingInstallments, setSavingInstallments] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadPackages();
    loadSiteSettings();
  }, [navigate]);

  const loadSiteSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/packages/site-settings`);
      if (res.data?.max_interest_free_installments !== undefined) {
        setMaxInterestFree(parseInt(res.data.max_interest_free_installments) || 2);
      }
    } catch {}
  };

  const saveMaxInstallments = async (value) => {
    setSavingInstallments(true);
    try {
      await axios.put(`${BACKEND_URL}/api/packages/site-settings`, {
        max_interest_free_installments: parseInt(value)
      });
      setMaxInterestFree(parseInt(value));
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setSavingInstallments(false);
    }
  };

  const loadPackages = async () => {
    try {
      setLoading(true);
      // Admin vê todos os pacotes, incluindo ocultos
      const response = await axios.get(`${BACKEND_URL}/api/packages/?active_only=false&include_hidden=true&include_hidden_dates=true`);
      setPackages(response.data);
    } catch (error) {
      console.error('Erro ao carregar pacotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (pkg) => {
    try {
      const newHiddenState = !pkg.is_hidden;
      await axios.put(`${BACKEND_URL}/api/packages/${pkg.id}/toggle-visibility?hidden=${newHiddenState}`);
      
      // Atualizar lista local
      setPackages(prev => prev.map(p => 
        p.id === pkg.id ? { ...p, is_hidden: newHiddenState } : p
      ));
    } catch (error) {
      console.error('Erro ao alterar visibilidade:', error);
      alert('Erro ao alterar visibilidade: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDuplicate = async (pkg) => {
    try {
      // Criar uma cópia do pacote com novo nome
      const duplicatedPackage = {
        ...pkg,
        name: `${pkg.name} (Cópia)`,
        dates: pkg.dates?.map(d => ({
          ...d,
          id: `d${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        })) || []
      };
      
      // Remover o ID original para criar um novo
      delete duplicatedPackage.id;
      delete duplicatedPackage.created_at;
      
      const newPkg = await packagesAPI.create(duplicatedPackage);
      
      // Redirecionar para edição do novo pacote
      navigate(`/admin/pacotes/editar/${newPkg.id}`);
    } catch (error) {
      console.error('Erro ao duplicar pacote:', error);
      alert('Erro ao duplicar pacote: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await packagesAPI.delete(id);
      setPackages(packages.filter(p => p.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Erro ao excluir pacote:', error);
      alert('Erro ao excluir pacote');
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountPercentage || isNaN(parseFloat(discountPercentage))) {
      alert('Informe um percentual válido');
      return;
    }
    
    setApplyingDiscount(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/packages/apply-global-discount?discount_percentage=${parseFloat(discountPercentage)}&category=${discountCategory}`
      );
      
      if (response.data.success) {
        alert(`Desconto de ${discountPercentage}% aplicado em ${response.data.updated_count} pacote(s)!`);
        setShowDiscountModal(false);
        setDiscountPercentage('');
        setDiscountCategory('all');
        loadPackages();
      }
    } catch (error) {
      console.error('Erro ao aplicar desconto:', error);
      alert('Erro ao aplicar desconto: ' + (error.response?.data?.detail || error.message));
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!window.confirm('Remover todos os descontos dos pacotes?')) return;
    
    setApplyingDiscount(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/packages/remove-global-discount?category=${discountCategory}`
      );
      
      if (response.data.success) {
        alert(`Desconto removido de ${response.data.updated_count} pacote(s)!`);
        setShowDiscountModal(false);
        loadPackages();
      }
    } catch (error) {
      console.error('Erro ao remover desconto:', error);
      alert('Erro ao remover desconto: ' + (error.response?.data?.detail || error.message));
    } finally {
      setApplyingDiscount(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'nacional-aereo': 'Aéreo Nacional',
      'internacional-aereo': 'Aéreo Internacional',
      'rodoviario': 'Rodoviário',
      'passeio-1-dia': 'Passeio de 1 Dia'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'nacional-aereo': 'bg-blue-100 text-blue-800',
      'internacional-aereo': 'bg-purple-100 text-purple-800',
      'rodoviario': 'bg-orange-100 text-orange-800',
      'passeio-1-dia': 'bg-green-100 text-green-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro de ano
    if (yearFilter !== 'all') {
      const hasYearDate = pkg.dates?.some(d => {
        const startDate = d.start_date;
        if (startDate) {
          const year = new Date(startDate).getFullYear();
          return year === parseInt(yearFilter);
        }
        return false;
      });
      if (!hasYearDate) return false;
    }
    
    return matchesSearch;
  });

  // Extrair anos únicos dos pacotes
  const availableYears = [...new Set(
    packages.flatMap(pkg => 
      pkg.dates?.map(d => {
        if (d.start_date) {
          return new Date(d.start_date).getFullYear();
        }
        return null;
      }).filter(Boolean) || []
    )
  )].sort();

  const getTotalSpots = (pkg) => {
    return pkg.dates?.reduce((acc, d) => acc + (d.spots_available || 0), 0) || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold">Gerenciar Pacotes</h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Parcelamento sem juros */}
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
              <span className="text-white text-xs whitespace-nowrap">Sem juros até:</span>
              <select
                value={maxInterestFree}
                onChange={(e) => saveMaxInstallments(e.target.value)}
                disabled={savingInstallments}
                className="bg-white text-gray-900 text-sm rounded px-2 py-1 font-bold w-16"
                data-testid="max-interest-free-select"
              >
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                  <option key={n} value={n}>{n}x</option>
                ))}
              </select>
            </div>
            <Button 
              onClick={() => setShowDiscountModal(true)}
              variant="outline" 
              className="text-white border-white hover:bg-white hover:text-[#003366]"
            >
              <Percent className="w-4 h-4 mr-2" />
              Desconto Global
            </Button>
            <Link to="/admin/pacotes/novo">
              <Button className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
                <Plus className="w-4 h-4 mr-2" />
                Novo Pacote
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Buscar pacote..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Ano" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Anos</SelectItem>
              {availableYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Packages List */}
        <div className="space-y-4">
          {filteredPackages.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-600">Nenhum pacote encontrado</p>
              </CardContent>
            </Card>
          ) : (
            filteredPackages.map((pkg) => (
              <Card key={pkg.id} className={`hover:shadow-md transition-shadow ${pkg.is_hidden ? 'opacity-60 border-dashed' : ''}`}>
                <CardContent className="p-4 sm:p-6">
                  {/* Layout responsivo - empilha no mobile, lado a lado no desktop */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    {/* Imagem e informações */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <img
                        src={pkg.main_image || pkg.image}
                        alt={pkg.name}
                        className="w-full sm:w-24 h-40 sm:h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-bold text-lg text-gray-900">{pkg.name}</h3>
                          <Badge className={getCategoryColor(pkg.category)}>
                            {getCategoryLabel(pkg.category)}
                          </Badge>
                          {pkg.active === false && (
                            <Badge variant="destructive">Inativo</Badge>
                          )}
                          {pkg.is_hidden && (
                            <Badge className="bg-gray-500 text-white">
                              <EyeOff className="w-3 h-3 mr-1" />
                              Oculto
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm mb-2 max-w-xl line-clamp-2">{pkg.short_description}</p>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {pkg.dates?.length || 0} datas
                          </span>
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {getTotalSpots(pkg)} vagas
                          </span>
                          {pkg.discount_percentage > 0 && pkg.original_price ? (
                            <span className="flex items-center gap-2">
                              <span className="line-through text-gray-400">{formatPrice(pkg.original_price)}</span>
                              <span className="font-bold text-green-600">{formatPrice(pkg.price)}</span>
                              <Badge className="bg-green-100 text-green-800">-{pkg.discount_percentage}%</Badge>
                            </span>
                          ) : (
                            <span className="font-semibold text-[#003366]">
                              {formatPrice(pkg.price)}
                            </span>
                          )}
                        </div>
                        {getTotalSpots(pkg) === 0 && (
                          <div className="flex items-center mt-2 text-red-600 text-sm">
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            Todas as vagas esgotadas
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Botões de ação - linha no mobile, coluna no desktop */}
                    <div className="flex flex-wrap sm:flex-nowrap items-center justify-start sm:justify-end gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-200">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        title={pkg.is_hidden ? "Mostrar para clientes" : "Ocultar de clientes"}
                        onClick={() => handleToggleVisibility(pkg)}
                        className={`${pkg.is_hidden ? 'text-green-600 border-green-600' : 'text-gray-600'}`}
                      >
                        {pkg.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <a href={`/pacote/${pkg.slug || pkg.id}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" title="Visualizar">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        title="Duplicar"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => handleDuplicate(pkg)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Link to={`/admin/pacotes/editar/${pkg.id}`}>
                        <Button variant="outline" size="sm" title="Editar">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        title="Excluir"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteConfirm(pkg.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Delete Confirmation */}
                  {deleteConfirm === pkg.id && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 mb-3">Tem certeza que deseja excluir "{pkg.name}"?</p>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(pkg.id)}
                        >
                          Sim, excluir
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Modal de Desconto Global */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-green-600" />
              Desconto Global
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Categoria</Label>
              <Select value={discountCategory} onValueChange={setDiscountCategory}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Pacotes</SelectItem>
                  <SelectItem value="nacional-aereo">Aéreo Nacional</SelectItem>
                  <SelectItem value="internacional-aereo">Aéreo Internacional</SelectItem>
                  <SelectItem value="rodoviario">Rodoviário</SelectItem>
                  <SelectItem value="passeio-1-dia">Passeio de 1 Dia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Percentual de Desconto (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="1"
                value={discountPercentage}
                onChange={(e) => setDiscountPercentage(e.target.value)}
                placeholder="Ex: 10, 15, 20"
                className="mt-1"
              />
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium mb-1">⚠️ Atenção:</p>
              <p>O desconto será aplicado sobre o preço original de cada pacote na categoria selecionada.</p>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowDiscountModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                variant="outline" 
                onClick={handleRemoveDiscount}
                disabled={applyingDiscount}
                className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
              >
                Remover Descontos
              </Button>
              <Button 
                onClick={handleApplyDiscount}
                disabled={applyingDiscount || !discountPercentage}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {applyingDiscount ? 'Aplicando...' : 'Aplicar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPackages;
